// src/components/layouts/app/header/mobile/MobileSideMenu.tsx
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  X, Laptop, Mouse, Cpu, HardDrive, Wrench, MapPin, Globe, User, LogOut, 
  ChevronRight, FileText, Building2, ShoppingBag, Briefcase, Hotel 
} from 'lucide-react';
import { CountryDropdown } from '../desktop/CountryDropdown';
import { LangCurrencyDropdown } from '../desktop/LangCurrencyDropdown';

interface MobileSideMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const sectors = [
  { icon: Laptop, label: 'Informatique & Bureautique', path: '/secteurs/informatique' },
  { icon: Building2, label: 'BTP & Énergie', path: '/secteurs/btp-energie' },
  { icon: ShoppingBag, label: 'Commerce & Distribution', path: '/secteurs/commerce-distribution' },
  { icon: Briefcase, label: 'Services & Consulting', path: '/secteurs/consulting' },
  { icon: Hotel, label: 'Hôtellerie & Restauration', path: '/secteurs/hotellerie' },
];

const categories = [
  { icon: Laptop, label: 'Ordinateurs portables', path: '/categories/ordinateurs-portables' },
  { icon: Mouse, label: 'Accessoires', path: '/categories/accessoires' },
  { icon: Cpu, label: 'Composants', path: '/categories/composants' },
  { icon: HardDrive, label: 'Réseau & stockage', path: '/categories/reseau-stockage' },
  { icon: Wrench, label: 'Support technique', path: '/support' }, // remplace l'ancien "Services"
];

const MobileSideMenu: React.FC<MobileSideMenuProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-white md:hidden"
        >
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="flex flex-col h-full"
          >
            {/* Header fixe */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-4 flex items-center justify-between z-10">
              <img src="/assets/logo/v2/logonissirec.png" alt="NISSI GROUP INTL" className="h-8" />
              <button
                onClick={onClose}
                className="p-2 -mr-2 rounded-full text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                aria-label="Fermer"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto px-4 py-6 space-y-8">
              {/* Section utilisateur */}
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-xl border border-primary/10">
                <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-800">Bonjour, invité</p>
                  <button className="text-sm text-primary font-medium">Se connecter / S'inscrire</button>
                </div>
              </div>

              {/* NOUVEAU : Nos secteurs d'activité (5 pôles) */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Nos secteurs</h3>
                <div className="space-y-2">
                  {sectors.map((sector, idx) => (
                    <Link
                      key={idx}
                      to={sector.path}
                      onClick={onClose}
                      className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-all duration-200 group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                          <sector.icon className="w-4 h-4 text-gray-600 group-hover:text-primary" />
                        </div>
                        <span className="text-gray-700 font-medium">{sector.label}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Bouton Devis express (CTA) */}
              <Link
                to="/devis"
                onClick={onClose}
                className="flex items-center justify-center gap-2 w-full py-3 bg-primary text-white rounded-xl font-semibold shadow-md hover:bg-primary-dark transition-all"
              >
                <FileText className="w-5 h-5" />
                <span>Devis express</span>
              </Link>

              {/* Catégories produits (e-commerce) */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Catégories</h3>
                <div className="space-y-2">
                  {categories.map((cat, idx) => (
                    <Link
                      key={idx}
                      to={cat.path}
                      onClick={onClose}
                      className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-all duration-200 group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                          <cat.icon className="w-4 h-4 text-gray-600 group-hover:text-primary" />
                        </div>
                        <span className="text-gray-700 font-medium">{cat.label}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Pays et langue */}
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Pays de livraison</span>
                  </div>
                  <CountryDropdown />
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Langue / Devise</span>
                  </div>
                  <LangCurrencyDropdown />
                </div>
              </div>

              {/* Liens rapides */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Liens rapides</h3>
                <div className="space-y-2">
                  <Link to="/offres" onClick={onClose} className="block p-3 rounded-xl hover:bg-gray-50 text-gray-700 font-medium transition-colors">
                    🔥 Offres
                  </Link>
                  <Link to="/support" onClick={onClose} className="block p-3 rounded-xl hover:bg-gray-50 text-gray-700 font-medium transition-colors">
                    📞 Support
                  </Link>
                  <Link to="/a-propos" onClick={onClose} className="block p-3 rounded-xl hover:bg-gray-50 text-gray-700 font-medium transition-colors">
                    À propos
                  </Link>
                </div>
              </div>

              {/* Déconnexion (si connecté) */}
              <button className="w-full flex items-center justify-center gap-2 py-3 text-red-600 border-t border-gray-100 mt-4 font-medium">
                <LogOut className="w-4 h-4" />
                <span>Déconnexion</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileSideMenu;